import { Link } from "react-router-dom";
import { Wind, ArrowUpRight, Check, Crown } from "lucide-react";
import { SERVICES, SEDATION_NOTE } from "@/data";
import { Reveal, SectionHead } from "@/components/Reveal";
import { BookButton } from "@/components/Buttons";

const ServiceCard = ({ service }) => (
  <Link
    to={`/services/${service.slug}`}
    data-testid={`service-card-${service.slug}`}
    className="group flex h-full flex-col overflow-hidden rounded-xl border border-stone/80 bg-cream transition-[border-color,box-shadow] duration-500 hover:border-forest/30 hover:shadow-[0_18px_40px_-24px_rgba(36,36,33,0.25)]"
  >
    <div className="relative aspect-[4/3] overflow-hidden bg-sage">
      <img
        src={service.image}
        alt={`${service.name} at Glebe Park Dental, Canberra City`}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
      />
    </div>
    <div className="flex flex-1 flex-col px-7 pb-7 pt-6">
      <p className="font-sans text-xs tracking-[0.18em] uppercase text-leafmute">{service.tag}</p>
      <h3 className="mt-2 font-serif text-2xl leading-snug text-ink">{service.name}</h3>
      <p className="mt-3 flex-1 font-sans text-base leading-relaxed text-fern">{service.blurb}</p>
      <span className="mt-6 inline-flex items-center gap-1.5 font-sans text-sm font-medium text-forest">
        Learn more
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
    </div>
  </Link>
);

const HIDDEN = ["teeth-whitening", "conscious-sedation"];

const ALIGNER_STATS = [
  ["Free", "first consult"],
  ["6–18", "months, start to finish"],
  ["Invisible", "to most people"],
];

const CROWN_STATS = [
  ["1 visit", "start to finish"],
  ["~2 hrs", "in the chair"],
  ["Ceramic", "matched to your teeth"],
];

const AlignerFeature = () => (
  <Reveal delay={0.1} className="mt-12">
    <div data-testid="clear-aligners-feature" className="group relative overflow-hidden rounded-2xl bg-forest shadow-[0_40px_80px_-40px_rgba(36,54,47,0.6)] transition-transform duration-500 hover:-translate-y-1">
      <img src="/services/clear-aligners.jpg" alt="Clear aligners at Glebe Park Dental, Canberra City" className="absolute inset-0 h-full w-full object-cover opacity-50 transition-transform duration-[1400ms] ease-out group-hover:scale-105" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-r from-forest via-forest/85 to-forest/20" />
      <div className="relative grid grid-cols-1 gap-10 px-8 py-12 sm:px-12 sm:py-16 lg:grid-cols-12 lg:px-16 lg:py-20">
        <div className="lg:col-span-8">
          <span className="inline-flex items-center gap-2 rounded-full bg-leaf px-4 py-1.5 font-sans text-sm font-semibold tracking-wide text-forest">
            <Check className="h-4 w-4" /> Free clear aligner consult — new patients
          </span>
          <h3 className="mt-6 max-w-2xl font-serif text-4xl leading-[1.05] text-cream sm:text-5xl lg:text-6xl">
            Straighter teeth. <span className="italic text-mist">No one will know how.</span>
          </h3>
          <p className="mt-6 max-w-xl font-sans text-lg leading-relaxed text-mist sm:text-xl">
            Clear aligners you take out to eat and brush — planned on a digital scan so you see your finished smile <span className="text-cream">before you spend a cent</span>. Come in, find out if you're a candidate, and leave with a straight answer.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <BookButton variant="light" testid="aligners-free-consult-button" className="px-8 py-4 text-lg">Book my free consult</BookButton>
            <Link to="/services/clear-aligners" data-testid="aligners-learn-more-link" className="group/l inline-flex items-center gap-2 font-sans text-base font-medium text-cream underline decoration-leaf/60 underline-offset-4 transition-colors hover:decoration-cream">
              How aligners work <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/l:translate-x-0.5 group-hover/l:-translate-y-0.5" />
            </Link>
          </div>
        </div>
        <dl className="grid grid-cols-3 gap-4 lg:col-span-4 lg:grid-cols-1 lg:self-end">
          {ALIGNER_STATS.map(([big, small]) => (
            <div key={small} className="rounded-xl border border-cream/15 bg-cream/5 px-5 py-4 backdrop-blur-sm">
              <dt className="font-serif text-3xl text-cream sm:text-4xl">{big}</dt>
              <dd className="mt-1 font-sans text-sm text-mist sm:text-base">{small}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  </Reveal>
);

const CrownFeature = () => (
  <Reveal delay={0.1} className="mt-6">
    <div data-testid="same-day-crowns-feature" className="group relative overflow-hidden rounded-2xl bg-forest shadow-[0_40px_80px_-40px_rgba(36,54,47,0.6)] transition-transform duration-500 hover:-translate-y-1">
      <img src="/services/same-day-crowns.jpg" alt="Same-day ceramic crowns at Glebe Park Dental, Canberra City" className="absolute inset-0 h-full w-full object-cover opacity-50 transition-transform duration-[1400ms] ease-out group-hover:scale-105" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-r from-forest via-forest/85 to-forest/20" />
      <div className="relative grid grid-cols-1 gap-10 px-8 py-12 sm:px-12 sm:py-16 lg:grid-cols-12 lg:px-16 lg:py-20">
        <div className="lg:col-span-8">
          <span className="inline-flex items-center gap-2 rounded-full bg-leaf px-4 py-1.5 font-sans text-sm font-semibold tracking-wide text-forest">
            <Crown className="h-4 w-4" /> One appointment — no temporary crown
          </span>
          <h3 className="mt-6 max-w-2xl font-serif text-4xl leading-[1.05] text-cream sm:text-5xl lg:text-6xl">
            A crown while you wait. <span className="italic text-mist">No messy moulds, no second visit.</span>
          </h3>
          <p className="mt-6 max-w-xl font-sans text-lg leading-relaxed text-mist sm:text-xl">
            We scan, design and mill your ceramic crown on site, then fit it the same day — <span className="text-cream">walk in with a broken tooth, out with a permanent one</span> that matches the rest of your teeth.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <BookButton variant="light" testid="crowns-book-button" className="px-8 py-4 text-lg">Book a crown appointment</BookButton>
            <Link to="/services/same-day-crowns" data-testid="crowns-learn-more-link" className="group/l inline-flex items-center gap-2 font-sans text-base font-medium text-cream underline decoration-leaf/60 underline-offset-4 transition-colors hover:decoration-cream">
              How same-day crowns work <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/l:translate-x-0.5 group-hover/l:-translate-y-0.5" />
            </Link>
          </div>
        </div>
        <dl className="grid grid-cols-3 gap-4 lg:col-span-4 lg:grid-cols-1 lg:self-end">
          {CROWN_STATS.map(([big, small]) => (
            <div key={small} className="rounded-xl border border-cream/15 bg-cream/5 px-5 py-4 backdrop-blur-sm">
              <dt className="font-serif text-3xl text-cream sm:text-4xl">{big}</dt>
              <dd className="mt-1 font-sans text-sm text-mist sm:text-base">{small}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  </Reveal>
);

const Services = () => (
    <section id="services" className="bg-sage py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <SectionHead
            dark
            overline="What we do"
            title={
              <>
                Pretty much everything —{" "}
                <span className="italic text-fern">without sending you across town.</span>
              </>
            }
          />
          <Reveal delay={0.2}>
            <BookButton testid="services-book-button" variant="outline">
              Book a check-up
            </BookButton>
          </Reveal>
        </div>

        <div data-testid="services-grid" className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {SERVICES.filter((s) => !HIDDEN.includes(s.slug)).map((s, i) => (
            <Reveal key={s.slug} delay={0.06 * (i % 3)} y={24}>
              <ServiceCard service={s} />
            </Reveal>
          ))}
        </div>

        <AlignerFeature />
        <CrownFeature />

        <Reveal delay={0.1} className="mt-8">
          <Link to="/services/conscious-sedation" data-testid="services-sedation-note" className="group flex items-start gap-4 rounded-xl border border-stone/80 bg-cream px-6 py-5 transition-[border-color] duration-300 hover:border-forest/30">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest text-leaf"><Wind className="h-4 w-4" /></span>
            <p className="font-sans text-sm leading-relaxed text-fern"><span className="font-medium text-ink">For anxious patients.</span> {SEDATION_NOTE} <span className="font-medium text-leafdeep group-hover:underline">How sedation works →</span></p>
          </Link>
        </Reveal>
      </div>
    </section>
);

export default Services;
